import { GridColDef } from "@mui/x-data-grid-pro";
import CreatePortfolioModal from "Pages/EquityEtf/ClientModal";
import {
  Alert,
  BigDotIcon,
  Box,
  Card,
  ErrorfilledIcon,
  Fade,
  GKButton,
  GKClientDataGrid,
  GKDatePicker,
  GKModal,
  GKSearchSelect,
  GKTextField,
  Grid2,
  InfolconIcon,
  RightIcon,
  Show,
  StatusField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Tooltip,
  Typography,
  UploadIcon,
} from "components";
import dayjs from "dayjs";
import { useUploadmanager } from "hooks/useUploadmanager";
import { FormProvider } from "react-hook-form";
import { handleRemoveUnderscore } from "utils";
import { moneyCommaSeparator } from "utils/MathFunction";
import { handleCapitalize } from "utils/common";
import { GKModalProps } from "utils/commonInterface";
import { extraColumnsField, matchDate } from "utils/helpers";
import AddFavoriteTemplate from "./AddFavoriteTemplate";
import UploadManagerStepperModal from "./UploadManagerStepperModal";
import UploadStepOne from "./UploadStepOne";

interface Props {
  fileData?: any;
  isApproveModal?: boolean;
}

const UploadManagerModal = ({
  open,
  setOpen,
  isApproveModal,
  fileData,
}: Props & GKModalProps) => {
  const {
    isLoading,
    handleReset,
    handleModalTitle,
    favoriteUploadSamples,
    setShowFavoriteModal,
    menuOptions,
    portfolios,
    isPortfolioCreateModalOpen,
    setIsPortfolioCreateModalOpen,
    companyNames,
    userInfo,
    showFavoriteModal,
    favoriteRefetch,
    downloadSingleRowErrors,
    handleDeleteFav,
    form,
    handleAddToFav,
    facLoading,
    handleNext,
    allFiles,
    setAllFiles,
    handleCheckPassword,
    selectedId,
    setSelectedId,
    handleCheckDate,
    handleReturnSize,
    setSelectedIndex,
    selectedIndex,
    handleReviewPortfolio,
    handleFileSubmission,
    handlePostPortfolioMapping,
    handlePostCompanyMapping,
    handleSheetReviewComplete,
    activeStepWatch,
    templatesTabspWatch,
  } = useUploadmanager({ setOpen, isApproveModal, fileData });

  const dataGridColumn: (GridColDef & { visibleType?: string[] })[] = [
    {
      ...extraColumnsField("Date", "date", 170, 1),
      valueFormatter: (params) => matchDate(params?.value),
      editable: false,
      visibleType: [
        "peq",
        "cash",
        "mf",
        "stock",
        "aif",
        "fno",
        "stock_as_capital",
      ],
      renderCell: (params) => {
        return (
          <Box display={"flex"} alignItems={"center"} gap={1}>
            <Tooltip title="Duplicate Entry" arrow placement="right">
              <InfolconIcon
                color={`var(--warning-700)`}
                height={20}
                width={20}
                style={{
                  visibility: params?.row?.isDuplicate ? "visible" : "hidden",
                }}
              />
            </Tooltip>
            <Typography>{params?.row?.date}</Typography>
          </Box>
        );
      },
    },
    {
      ...extraColumnsField("Portfolio", "Portfolio", 170, 1),
      editable: false,
      valueGetter: (params: any) =>
        `${params.row.portfolioName} / ${params.row.portfolioCode}`,
      visibleType: [
        "peq",
        "cash",
        "mf",
        "stock",
        "portfolio",
        "margin",
        "aif",
        "fno",
        "stock_as_capital",
      ],
    },
    {
      ...extraColumnsField("Client name", "clientName", 150, 1),
      visibleType: ["portfolio"],
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.name || "-";
      },
    },
    {
      ...extraColumnsField("Transaction Type", "transactionType", 170, 1),
      editable: false,
      visibleType: ["peq", "cash", "mf", "stock", "aif", "fno"],
      valueFormatter: (params) => handleCapitalize(params?.value),
    },
    {
      ...extraColumnsField(
        "Transaction Sub Type",
        "subTransactionType",
        150,
        1,
      ),
      valueFormatter: (params) => handleCapitalize(params?.value),
      visibleType: ["cash"],
      renderCell: (data: any) =>
        handleRemoveUnderscore(handleCapitalize(data?.value)) || " ",
    },
    {
      visibleType: ["peq", "mf", "stock", "aif", "fno", "stock_as_capital"],
      ...extraColumnsField("Company Name", "companyName", 170, 1),
      editable: false,
    },
    {
      visibleType: ["stock", "stock_as_capital"],
      ...extraColumnsField("BSE / NSE Code", "bseCode", 190, 1),
      editable: false,
      valueGetter: (params: any) =>
        `${params.row.bseCode || "-"} / ${params.row.nseCode || "-"}`,
    },
    {
      visibleType: ["cash"],
      ...extraColumnsField("Remarks", "remarks", 150, 1),
      renderCell: (data: any) => data?.value || " ",
    },
    {
      visibleType: ["mf"],
      ...extraColumnsField("Folio No", "folioNo", 150, 1),
      editable: false,
    },
    {
      visibleType: ["mf"],
      ...extraColumnsField("Company Code", "isinCode", 170, 1),
      editable: false,
      valueGetter: (params: any) => `${params.row.isinCode || "-"}`,
    },
    {
      visibleType: ["mf", "stock", "stock_as_capital"],
      ...extraColumnsField("Quantity", "quantity", 150, 1),
      editable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      visibleType: ["peq", "mf", "stock", "aif", "fno", "stock_as_capital"],
      ...extraColumnsField("Price", "price", 150, 1),
      editable: false,
      align: "right",
      headerAlign: "right",
    },
    {
      ...extraColumnsField("Amount", "billAmount", 150, 1),
      valueGetter: (data: any) => moneyCommaSeparator(data?.value),
      visibleType: [
        "peq",
        "cash",
        "mf",
        "stock",
        "aif",
        "fno",
        "stock_as_capital",
      ],
      align: "right",
      headerAlign: "right",
    },
    {
      visibleType: ["peq", "mf", "stock", "aif", "fno", "stock_as_capital"],
      ...extraColumnsField("STT", "stt", 170, 1),
      valueFormatter: (params: any) => moneyCommaSeparator(params?.value),
      editable: false,
    },
    {
      visibleType: ["peq", "mf", "stock", "aif", "fno", "stock_as_capital"],
      ...extraColumnsField("Stamp Charges", "stampCharges", 170, 1),
      editable: false,
      valueFormatter: (params: any) => moneyCommaSeparator(params?.value),
    },
    {
      visibleType: ["peq", "mf", "stock", "aif", "fno"],
      ...extraColumnsField("Broker Name", "brokerName", 170, 1),
      editable: false,
    },
    {
      visibleType: ["peq", "stock", "aif"],
      ...extraColumnsField(
        "Buy Back Payout",
        "buyPayout",
        form.getValues("fileObject.version") === "BUY_BACK_CSV" ? 0 : 120,
        1,
      ),
    },
    {
      visibleType: ["peq", "stock", "aif", "fno", "stock_as_capital"],
      ...extraColumnsField("Brokerage", "brokerage", 150, 1),
      align: "right",
      headerAlign: "right",
    },
    {
      visibleType: ["peq", "stock", "aif", "fno", "stock_as_capital"],
      ...extraColumnsField(
        "Amount With Brokerage",
        "amountWithBrokerage",
        200,
        1,
      ),
      editable: false,
      align: "right",
      headerAlign: "right",
      valueFormatter: (params: any) => moneyCommaSeparator(params?.value),
    },
    {
      visibleType: ["peq", "stock", "aif", "fno", "stock_as_capital"],
      ...extraColumnsField("GST", "gst", 170, 1),
      valueFormatter: (params: any) => moneyCommaSeparator(params?.value),
      editable: false,
    },

    {
      visibleType: ["peq", "stock", "aif", "fno", "stock_as_capital"],
      ...extraColumnsField("Transaction Charges", "transactionCharges", 170, 1),
      editable: false,
      valueFormatter: (params: any) => moneyCommaSeparator(params?.value),
    },
    {
      visibleType: ["peq", "stock", "aif", "stock_as_capital"],
      ...extraColumnsField("Sebi Turnover Fees", "sebiTurnoverFees", 170, 1),
      editable: false,
      valueFormatter: (params: any) => moneyCommaSeparator(params?.value),
    },
    {
      visibleType: ["peq", "stock", "aif", "fno", "stock_as_capital"],
      ...extraColumnsField("Other Charges", "otherCharges", 170, 1),
      align: "right",
      headerAlign: "right",
      valueGetter: (params) => params.row.otherCharges || "0",
      valueFormatter: (params) => moneyCommaSeparator(params.value),
    },

    {
      visibleType: ["stock"],
      ...extraColumnsField("Contract Note Number", "contractNoteNum", 170, 1),
      editable: false,
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Phone Number", "phoneNumber", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.phone || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("PAN", "pan", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.pan || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Email", "email", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.email || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Joining Date", "joiningDate", 150, 1),
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Billing Cycle", "billingCycle", 150, 1),
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("AUM Fee", "aumFee", 150, 1),
      align: "right",
      headerAlign: "right",
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Performance Fee", "phoperformanceFeene", 150, 1),
      align: "right",
      headerAlign: "right",
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Soft Hurdle", "softHurdle", 150, 1),
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Hard Hurdle", "hardHurdle", 150, 1),
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Date Of Birth", "dateOfBirth", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.dateOfBirth || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Gender", "gender", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.gender || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Marital Status", "maritalStatus", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.maritalStatus || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Father's Name", "fatherName", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.fatherName || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Mother's Name", "motherName", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.motherName || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Family Name", "familyName", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.family[0]?.name || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Nominee Added", "nominees", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.nominees?.length || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Address", "address", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.address || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Occupation", "occupation", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.occupation || "-";
      },
    },
    {
      visibleType: ["portfolio"],
      ...extraColumnsField("Income", "income", 150, 1),
      valueGetter: (params) => {
        return params?.row?.client?.personalDetails?.income || "-";
      },
    },
    {
      visibleType: ["margin"],
      ...extraColumnsField("Available Margin", "brokerLedgerBalance", 150, 1),
      valueFormatter: (params) => {
        return moneyCommaSeparator(params.value || 0) || "-";
      },
    },
    {
      visibleType: [
        "peq",
        "cash",
        "mf",
        "stock",
        "portfolio",
        "margin",
        "aif",
        "fno",
        "stock_as_capital",
      ],
      ...extraColumnsField("Error", "error", 150, 1),
      renderCell: (data) => {
        return (
          <Tooltip placement="left-start" arrow title={data?.row?.error || "-"}>
            <Typography>{data?.row?.error || "-"}</Typography>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <GKModal
      modalTitle={"Import"}
      open={open}
      setOpen={setOpen}
      width={"70%"}
      maxHeight="600px"
      footer={
        <>
          <Show.When
            isTrue={
              templatesTabspWatch === 2 &&
              form.getValues("fileBrokerType") !== ""
            }
          >
            <Grid2 container justifyContent={"space-between"} size={12}>
              <GKButton
                variant="outlined"
                color="dark"
                onClick={() => {
                  form.setValue("activeStep", 0);
                  form.reset();
                  handleReset();
                  setAllFiles([]);
                  setSelectedIndex([]);
                }}
              >
                Cancel
              </GKButton>
              <div
                style={{
                  display: "flex",
                  gap: 8,
                }}
              >
                <GKButton
                  variant="outlined"
                  color="dark"
                  disabled={
                    form.getValues("file").length === 0 ||
                    !form.getValues("fileVersion") ||
                    !form.getValues("fileBrokerType") ||
                    !form.getValues("fileType")
                  }
                  onClick={() => {
                    if (form.getValues("fileObject.isPasswordRequired")) {
                      handleCheckPassword(allFiles);
                    }
                    if (form.getValues("fileObject.isDateRequired")) {
                      handleCheckDate(allFiles);
                    }
                    handleNext();
                    form.setValue("templatesTabs", 0);
                    setAllFiles(
                      allFiles?.map((data) => ({
                        ...data,
                        inProgress: false,
                      })),
                    );
                  }}
                  startIcon={<UploadIcon />}
                >
                  Import Files
                </GKButton>
              </div>
            </Grid2>
          </Show.When>
          <Show.When isTrue={templatesTabspWatch !== 2}>
            <Grid2
              container
              justifyContent={
                activeStepWatch !== 0 ? "space-between" : "flex-end"
              }
              gap={1}
              size={12}
            >
              {activeStepWatch !== 0 && (
                <GKButton
                  variant="outlined"
                  color="dark"
                  size={"medium"}
                  onClick={() => {
                    form.setValue("activeStep", 0);
                    form.reset();
                    handleReset();
                    setSelectedIndex([]);
                    setAllFiles([]);
                  }}
                >
                  Cancel
                </GKButton>
              )}
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  gap: 8,
                }}
              >
                {[0].includes(form.getValues("activeStep")) && (
                  <GKButton
                    variant="contained"
                    size={"medium"}
                    disabled={
                      form.getValues("file").length === 0 ||
                      !form.getValues("fileVersion") ||
                      !form.getValues("fileBrokerType") ||
                      !form.getValues("fileType")
                    }
                    endIcon={<RightIcon />}
                    onClick={() => {
                      if (form.getValues("fileObject.isPasswordRequired")) {
                        handleCheckPassword(allFiles);
                      }
                      if (
                        form.getValues("fileVersion") ===
                        "MOTILAL__XLSX_BULK__V0"
                      ) {
                        handleCheckDate(allFiles);
                      }
                      setAllFiles(
                        allFiles?.map((data) => ({
                          ...data,
                          inProgress: false,
                        })),
                      );
                      handleNext();
                    }}
                  >
                    Next
                  </GKButton>
                )}
                {activeStepWatch === 6 && (
                  <GKButton
                    variant="contained"
                    size={"medium"}
                    onClick={() => {
                      form.setValue("activeStep", 0);
                      form.reset();
                      setShowFavoriteModal(false);
                      setIsPortfolioCreateModalOpen(false);
                      handleReset();
                      setOpen(false);
                      setAllFiles([]);
                      setSelectedIndex([]);
                    }}
                    disabled={
                      allFiles?.filter((data) => !data?.finished)?.length !== 0
                    }
                  >
                    Finish
                  </GKButton>
                )}
                {form.getValues("fileUploadType") !== "Portfolio" &&
                  [2, 3, 4].includes(form.getValues("activeStep")) && (
                    <GKButton
                      variant="contained"
                      size={"medium"}
                      type="submit"
                      form={
                        handleModalTitle(form.getValues("activeStep"))?.form
                      }
                      disabled={
                        form.getValues("file")?.length === 0 ||
                        form.getValues("fileUploadType") === "" ||
                        (form.getValues("fileUploadType") === "Stock" &&
                          form.getValues("fileVersion") === "")
                      }
                    >
                      Next
                    </GKButton>
                  )}
                {activeStepWatch === 4 &&
                  form.getValues("fileUploadType") === "Portfolio" && (
                    <GKButton
                      onClick={() => {
                        form.setValue("activeStep", 5);
                        handleReviewPortfolio(
                          form.getValues("fileReviewResponse.transactions"),
                        );
                      }}
                      variant="contained"
                      size={"medium"}
                      loading={isLoading}
                    >
                      <Typography>Finish</Typography>
                    </GKButton>
                  )}
              </div>
            </Grid2>
          </Show.When>
        </>
      }
    >
      <FormProvider {...form}>
        {activeStepWatch === 0 && (
          <UploadStepOne
            id="upload-manager-form"
            favoriteUploadSamples={favoriteUploadSamples}
            setShowFavoriteModal={setShowFavoriteModal}
            menuOptions={menuOptions}
            facLoading={facLoading}
            showFavoriteModal={showFavoriteModal}
            favoriteRefetch={favoriteRefetch}
            handleDeleteFav={handleDeleteFav}
            handleAddToFav={handleAddToFav}
            setAllFiles={setAllFiles}
            selectedId={selectedId}
            setSelectedId={setSelectedId}
          />
        )}
        {activeStepWatch !== 0 &&
          allFiles?.map((dataItem: any, index: any) => {
            return (
              <Grid2 container>
                <Show.When isTrue={!isApproveModal}>
                  <Grid2 size={12}>
                    <Card
                      sx={{
                        borderColor: "var(--neutral-100)",
                        my: 1,
                        p: 1,
                        borderRadius: 2,
                      }}
                    >
                      <Grid2 container alignItems={"center"}>
                        <Grid2
                          container
                          alignContent={"center"}
                          gap={1}
                          size={8}
                        >
                          <Grid2>
                            <img
                              src={`/FileIcons/${dataItem?.file?.name
                                .split(".")
                                .pop()}.svg`}
                              height={40}
                              width={40}
                              alt=""
                              style={{
                                borderRadius: 4,
                                border: "1px solid",
                                borderColor: "var(--neutral-100)",
                                padding: 8,
                              }}
                            />
                          </Grid2>
                          <Grid2>
                            <Typography>{dataItem?.file?.name}</Typography>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              <Typography
                                color={"var(--neutral-500)"}
                                fontSize={12}
                              >
                                {handleReturnSize(dataItem?.file)}
                              </Typography>
                              <Show.When isTrue={dataItem?.data}>
                                <BigDotIcon
                                  width={20}
                                  height={20}
                                  color={"var(--neutral-500)"}
                                />
                              </Show.When>
                              <Show.When isTrue={dataItem?.data}>
                                <Typography
                                  color={"var(--neutral-500)"}
                                  fontSize={12}
                                >
                                  {dataItem?.data?.remarks}
                                </Typography>
                              </Show.When>
                            </div>
                          </Grid2>
                        </Grid2>
                        <Grid2
                          size={4}
                          display={"flex"}
                          justifyContent={"flex-end"}
                          alignItems={"center"}
                        >
                          <Show.When
                            isTrue={
                              dataItem?.isDateRequired &&
                              !dataItem?.inProgress &&
                              !dataItem?.finished
                            }
                          >
                            <Grid2 mr={1}>
                              <GKDatePicker
                                value={dayjs(dataItem?.date)}
                                onChange={(event) => {
                                  setAllFiles(
                                    allFiles?.map(
                                      (item: any, itemIndex: number) => {
                                        return {
                                          ...item,
                                          date:
                                            itemIndex === index
                                              ? dayjs(event)
                                              : "",
                                        };
                                      },
                                    ),
                                  );
                                }}
                              />
                            </Grid2>
                          </Show.When>
                          <Show.When
                            isTrue={
                              dataItem?.hasPassword &&
                              !dataItem?.inProgress &&
                              !dataItem?.finished
                            }
                          >
                            <Grid2 mr={1}>
                              <GKTextField
                                placeholder="Password"
                                sx={{
                                  "& .MuiInputBase-input": {
                                    p: 1,
                                  },
                                }}
                                onChange={(event) => {
                                  setAllFiles(
                                    allFiles?.map(
                                      (item: any, itemIndex: number) => {
                                        return {
                                          ...item,
                                          password:
                                            itemIndex === index
                                              ? event?.target?.value
                                              : "",
                                        };
                                      },
                                    ),
                                  );
                                }}
                              />
                            </Grid2>
                          </Show.When>
                          <Show.When isTrue={!dataItem?.data?.id}>
                            <GKButton
                              disabled={isLoading || dataItem?.inProgress}
                              variant="contained"
                              onClick={() => {
                                form.setValue("file", [dataItem?.file]);
                                form.setValue("fileIndex", index);
                                form.setValue(
                                  "filePassword",
                                  dataItem?.password,
                                );
                                form.setValue("date", dataItem?.date);

                                const appendedFiles = new FormData();
                                appendedFiles.append(
                                  form.getValues("fileUploadType") ===
                                    "Portfolio"
                                    ? "file"
                                    : "files",
                                  dataItem.file,
                                  dataItem.file.name,
                                );
                                const formatedDate = dayjs(
                                  form.getValues("date"),
                                ).format("YYYY-MM-DD");
                                appendedFiles.append(
                                  "password",
                                  form.getValues("filePassword"),
                                );
                                appendedFiles.append("date", formatedDate);
                                handleFileSubmission({
                                  formData: appendedFiles,
                                  fileIndex: index,
                                });

                                setAllFiles(
                                  allFiles?.map((data, i) => ({
                                    ...data,
                                    inProgress: index === i,
                                  })),
                                );
                              }}
                            >
                              Import
                            </GKButton>
                          </Show.When>
                          <Show.When isTrue={dataItem?.data?.id}>
                            <StatusField
                              statusString={
                                dataItem?.data?.status || "Importing"
                              }
                            />
                          </Show.When>
                        </Grid2>
                      </Grid2>
                    </Card>
                  </Grid2>
                </Show.When>
                <Show.When
                  isTrue={isLoading && form.getValues("fileIndex") === index}
                >
                  <Grid2 size={12}>
                    <Fade in={isLoading}>
                      <Card
                        sx={{
                          borderColor: "var(--neutral-100)",
                          my: 1,
                          p: 1,
                          borderRadius: 2,
                          borderStyle: "dashed",
                        }}
                      >
                        <Grid2
                          container
                          justifyContent={"center"}
                          alignItems={"center"}
                        >
                          <Grid2 size={12} textAlign={"center"}>
                            <Typography
                              fontSize={16}
                              color={"var(--neutral-500)"}
                            >
                              Please wait, your file is being processed...
                            </Typography>
                          </Grid2>
                          <Grid2 size={12} textAlign={"center"}>
                            <UploadManagerStepperModal
                              activeStep={form.getValues("activeStep")}
                              loading={isLoading}
                            />
                          </Grid2>
                        </Grid2>
                      </Card>
                    </Fade>
                  </Grid2>
                </Show.When>
                <Show.When
                  isTrue={
                    !isLoading &&
                    activeStepWatch === 2 &&
                    form.getValues("fileIndex") === index
                  }
                >
                  <Grid2 size={12}>
                    <form
                      onSubmit={form.handleSubmit((values) => {
                        handlePostPortfolioMapping(
                          values.nullPortfolios?.map((item) => {
                            return {
                              value: item.value,
                              portfolio: item.portfolio,
                            };
                          }),
                        );
                      })}
                      id={"portfolio-mapping-form"}
                      style={{
                        width: "100%",
                      }}
                    >
                      <TableContainer>
                        <Table>
                          <TableBody>
                            <TableRow>
                              <TableCell
                                sx={{
                                  display: "flex",
                                  justifyContent: "space-between",
                                  alignItems: "center",
                                }}
                              >
                                <Typography fontSize={16} />
                                <GKButton
                                  variant="outlined"
                                  size="small"
                                  onClick={() =>
                                    setIsPortfolioCreateModalOpen(true)
                                  }
                                  title="Add New Portfolio"
                                >
                                  Create New Portfolio
                                </GKButton>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                <Alert
                                  sx={{ mb: 2 }}
                                  severity="warning"
                                  icon={<ErrorfilledIcon width={14} />}
                                >
                                  The Following portfolio codes we have not
                                  found in your account, please map it to
                                  already existing portfolios.
                                </Alert>
                                {form.getValues("nullPortfolios") &&
                                  form.getValues("nullPortfolios")?.length !==
                                    0 &&
                                  form
                                    .getValues("nullPortfolios")
                                    .map((item: any, index: number) => (
                                      <Grid2 size={12} my={1} key={index}>
                                        <GKSearchSelect
                                          disableClearable
                                          renderOption={(props, option) => {
                                            return (
                                              <li
                                                {...props}
                                                style={{
                                                  display: "flex",
                                                  flexDirection: "column",
                                                  alignItems: "flex-start",
                                                }}
                                              >
                                                <Typography fontWeight={600}>
                                                  {option.clientName}
                                                </Typography>
                                                <Typography>
                                                  {option.name}
                                                </Typography>
                                              </li>
                                            );
                                          }}
                                          options={portfolios || []}
                                          getOptionLabel={(option) =>
                                            option?.name || option
                                          }
                                          inputLabel={item?.value}
                                          onChange={(e, val) => {
                                            form.setValue(
                                              `nullPortfolios.${index}.portfolio`,
                                              val?.value,
                                            );
                                          }}
                                          value={
                                            portfolios?.find(
                                              (data: any) =>
                                                data?.id ===
                                                form.getValues(
                                                  "nullPortfolios",
                                                )[index].value,
                                            )?.name
                                          }
                                        />
                                      </Grid2>
                                    ))}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </form>
                  </Grid2>
                </Show.When>
                <Show.When
                  isTrue={
                    !isLoading &&
                    activeStepWatch === 3 &&
                    form.getValues("fileIndex") === index
                  }
                >
                  <Grid2 size={12}>
                    <form
                      onSubmit={form.handleSubmit((values) => {
                        handlePostCompanyMapping(values.nullCompanies);
                      })}
                      id={"company-mapping-form"}
                    >
                      <TableContainer>
                        <Table>
                          <TableBody>
                            <TableRow>
                              <TableCell>
                                <Typography fontSize={16}>
                                  Company Mapping
                                </Typography>
                              </TableCell>
                            </TableRow>
                            <TableRow>
                              <TableCell>
                                <Alert sx={{ mb: 2 }} severity="info">
                                  We couldn&#39;t find the following codes,
                                  please map them to the right companies.
                                </Alert>
                                {form.getValues("nullCompanies") &&
                                  form.getValues("nullCompanies")?.length !==
                                    0 &&
                                  form
                                    .getValues("nullCompanies")
                                    .map((item: any, index: number) => (
                                      <Grid2 size={12} my={1} key={index}>
                                        <GKSearchSelect
                                          disableClearable
                                          renderOption={(props, option) => (
                                            <li
                                              {...props}
                                              style={{
                                                display: "flex",
                                                alignItems: "center",
                                                gap: 4,
                                              }}
                                            >
                                              <Typography
                                                color={"var(--white)"}
                                                fontSize={8}
                                                style={{
                                                  background:
                                                    option?.instType === "EQ"
                                                      ? "var(--secondary-900)"
                                                      : "var(--orange-700)",
                                                  padding: "2px 4px",
                                                  borderRadius: 4,
                                                }}
                                              >
                                                {option?.instType}
                                              </Typography>
                                              <Typography whiteSpace={"nowrap"}>
                                                {option?.name}
                                              </Typography>
                                            </li>
                                          )}
                                          options={
                                            companyNames?.data?.data || []
                                          }
                                          getOptionLabel={(option) =>
                                            option?.companyName || option
                                          }
                                          inputLabel={item?.advisorCompany}
                                          onChange={(e, val) => {
                                            form.setValue(
                                              `nullCompanies.${index}.value`,
                                              val.gridCode,
                                            );
                                            form.setValue(
                                              `nullCompanies.${index}.nseBseCode`,
                                              val.bse || val.nse,
                                            );
                                            form.setValue(
                                              `nullCompanies.${index}.company`,
                                              userInfo.companyId,
                                            );
                                          }}
                                          value={
                                            companyNames?.data?.data?.find(
                                              (data: any) =>
                                                data?.gridCode ===
                                                form.getValues("nullCompanies")[
                                                  index
                                                ].value,
                                            )?.companyName
                                          }
                                        />
                                      </Grid2>
                                    ))}
                              </TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </form>
                  </Grid2>
                </Show.When>
                <Show.When
                  isTrue={
                    !isLoading &&
                    activeStepWatch === 4 &&
                    form.getValues("fileIndex") === index
                  }
                >
                  <form
                    id="review-completed-form"
                    style={{ width: "-webkit-fill-available" }}
                    onSubmit={form.handleSubmit(() => {
                      handleSheetReviewComplete(selectedIndex);
                    })}
                  >
                    <Grid2 size={12} container spacing={1}>
                      <Grid2
                        size={12}
                        sx={{
                          display:
                            form
                              .getValues("fileReviewResponse.transactions")
                              ?.filter((data: any) => data.isDuplicate)
                              ?.length === 0
                              ? "none"
                              : "flex",
                        }}
                      >
                        <Alert
                          severity="warning"
                          icon={<InfolconIcon width={14} height={14} />}
                          style={{
                            width: "100%",
                          }}
                        >
                          There are duplicate found in the file that you have
                          uploaded please review your sheet
                        </Alert>
                      </Grid2>
                      <Grid2
                        size={12}
                        sx={{
                          display:
                            form
                              .getValues("fileReviewResponse.transactions")
                              ?.filter((data: any) => data.error !== "")
                              ?.length === 0
                              ? "none"
                              : "flex",
                          justifyContent: "space-between",
                        }}
                      >
                        <Alert
                          sx={{ width: "100%" }}
                          severity="error"
                          icon={<InfolconIcon width={14} height={14} />}
                        >
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                              alignItems: "center",
                            }}
                          >
                            <Typography>
                              There are errors found in the sheet. Please review
                              them and re-upload the file.
                            </Typography>
                            {form.getValues("fileUploadType") !==
                              "Portfolio" && (
                              <GKButton
                                style={{
                                  marginLeft: 10,
                                  padding: 0,
                                }}
                                color="error"
                                variant="link"
                                onClick={() => {
                                  downloadSingleRowErrors(
                                    form.getValues("fileReviewResponse.id"),
                                    form.getValues("file")[0]?.name,
                                  );
                                }}
                              >
                                Download Errors
                              </GKButton>
                            )}
                          </div>
                        </Alert>
                      </Grid2>
                      <Grid2 size={12}>
                        <GKClientDataGrid
                          name="uploadManager"
                          density="compact"
                          pagination={false}
                          disableRowSelectionOnClick
                          keepNonExistentRowsSelected
                          checkboxSelection
                          maxHeight={"calc(100vh - 500px)"}
                          rows={
                            form.getValues("fileReviewResponse.transactions") ||
                            []
                          }
                          rowSelectionModel={selectedIndex}
                          onRowSelectionModelChange={(params) => {
                            setSelectedIndex(params);
                          }}
                          headerTitle={
                            <div style={{ marginRight: 8 }}>
                              <Typography
                                fontSize={14}
                                fontWeight={600}
                                whiteSpace={"nowrap"}
                              >
                                Review Imported data
                              </Typography>
                              <Typography
                                whiteSpace={"nowrap"}
                                fontSize={12}
                                color={"var(--neutral-500)"}
                              >
                                Total Entries:{" "}
                                {form.getValues(
                                  "fileReviewResponse.transactions",
                                )?.length || "0"}
                              </Typography>
                            </div>
                          }
                          headerComponent={
                            form.getValues("fileReviewResponse.transactions")
                              ?.length > 0 ? (
                              <Grid2
                                display={"flex"}
                                alignItems={"center"}
                                gap={1}
                              >
                                <Typography
                                  fontSize={14}
                                  fontWeight={500}
                                  whiteSpace={"nowrap"}
                                >
                                  Holding as of :
                                </Typography>
                                <Typography whiteSpace={"nowrap"}>
                                  {
                                    form.getValues(
                                      "fileReviewResponse.transactions",
                                    )[0]?.date
                                  }
                                </Typography>
                              </Grid2>
                            ) : null
                          }
                          columns={
                            form.getValues("fileReviewResponse.transactions") &&
                            dataGridColumn?.filter((d) =>
                              d.visibleType.includes(
                                form
                                  .getValues("fileObject")
                                  .uploadFile?.toLowerCase(),
                              ),
                            )
                          }
                          initialState={{
                            columns: {
                              columnVisibilityModel: {
                                stt: false,
                                gst: false,
                                stampCharges: false,
                                otherCharges: false,
                                transactionCharges: false,
                                sebiTurnoverFees: false,
                              },
                            },
                            pagination: {
                              paginationModel: {
                                pageSize: 25,
                              },
                            },
                          }}
                        />
                      </Grid2>
                    </Grid2>
                  </form>
                </Show.When>
              </Grid2>
            );
          })}

        {isPortfolioCreateModalOpen && (
          <CreatePortfolioModal
            isModalOpen={isPortfolioCreateModalOpen}
            setIsModalOpen={setIsPortfolioCreateModalOpen}
          />
        )}

        {showFavoriteModal && (
          <AddFavoriteTemplate
            open={showFavoriteModal}
            setOpen={setShowFavoriteModal}
            favoriteRefetch={favoriteRefetch}
            otherData={form.getValues()}
          />
        )}
      </FormProvider>
    </GKModal>
  );
};

export default UploadManagerModal;
